import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, FileCheck, MapPin, Calendar, User, Shirt, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import PrintEntregaButtons from '../conductor/PrintEntregaButtons';

const DetalleEntregaVerticalModal = ({ isOpen, onClose, entrega }) => {
  if (!entrega) return null;

  // El comprobante de entrega solo aplica cuando ya fue entregado.
  // En otro estado se muestra/imprime un ticket de alistamiento.
  const esEntregado = entrega.estado === 'entregado';

  const getTotalItems = () => {
    return entrega.items?.reduce((sum, i) => sum + (i.cantidad || 0), 0) || 0;
  };

  const getTotalKilos = () => {
    const totalGramos = entrega.items?.reduce((sum, i) => {
      const peso = i.peso || 0;
      const cantidad = i.cantidad || 0;
      return sum + (peso * cantidad);
    }, 0) || 0;
    return (totalGramos / 1000).toFixed(2);
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'N/A';
    return new Date(fecha).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Preparar datos en formato compatible con PrintEntregaButtons
  const solicitudFormateada = {
    ...entrega,
    tipo: 'lenceria',
    displayName: entrega.edificio?.nombre || 'Sin edificio',
    fechaEntrega: entrega.fecha_entrega,
    entregadoPor: entrega.conductor || entrega.alistador,
    firmaDigital: entrega.firma,
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <FileCheck className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      {esEntregado ? 'Comprobante de Entrega' : 'Ticket de Alistamiento'}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Lencería Vertical
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  className="rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
                {/* Información de la entrega */}
                <div className="space-y-4 mb-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                      <MapPin className="h-5 w-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Edificio</p>
                        <p className="font-semibold text-sm">{entrega.edificio?.nombre}</p>
                        <p className="text-xs text-gray-600">{entrega.edificio?.ciudad}</p>
                      </div>
                    </div>

                    {(entrega.hospedaje?.nombre || entrega.hospedaje?.nombrePropiedad) && (
                      <div className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                        <Home className="h-5 w-5 text-green-600 mt-0.5" />
                        <div>
                          <p className="text-xs text-gray-600 dark:text-gray-400">Activo</p>
                          <p className="font-semibold text-sm">
                            {entrega.hospedaje?.nombre || entrega.hospedaje?.nombrePropiedad}
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                      <Calendar className="h-5 w-5 text-green-600 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {esEntregado ? 'Fecha de Entrega' : 'Fecha de Alistamiento'}
                        </p>
                        <p className="font-semibold text-sm">
                          {formatFecha(esEntregado ? entrega.fecha_entrega : entrega.fecha_alistamiento)}
                        </p>
                      </div>
                    </div>

                    {entrega.alistador && (
                      <div className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                        <User className="h-5 w-5 text-purple-600 mt-0.5" />
                        <div>
                          <p className="text-xs text-gray-600 dark:text-gray-400">Alistador</p>
                          <p className="font-semibold text-sm">
                            {entrega.alistador?.nombre || entrega.alistador || 'N/A'}
                          </p>
                        </div>
                      </div>
                    )}

                    {esEntregado && (
                      <div className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                        <User className="h-5 w-5 text-orange-600 mt-0.5" />
                        <div>
                          <p className="text-xs text-gray-600 dark:text-gray-400">Entregado por</p>
                          <p className="font-semibold text-sm">
                            {entrega.conductor?.nombre || entrega.conductor || 'Conductor'}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Aviso de estado pendiente */}
                  {!esEntregado && (
                    <div className="flex items-start space-x-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <FileCheck className="h-5 w-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Estado</p>
                        <p className="font-semibold text-sm text-blue-700 dark:text-blue-400">
                          Pendiente de entrega · Documento interno de alistamiento
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Receptor */}
                  {entrega.receptor_nombre && (
                    <div className="flex items-start space-x-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                      <User className="h-5 w-5 text-green-600 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Recibido por</p>
                        <p className="font-semibold text-sm text-green-700 dark:text-green-400">
                          {entrega.receptor_nombre}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Observaciones */}
                  {entrega.observaciones && (
                    <div className="col-span-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Observaciones</p>
                      <p className="text-sm text-gray-900 dark:text-gray-100">
                        {entrega.observaciones}
                      </p>
                    </div>
                  )}
                </div>

                {/* Items entregados */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                      <Shirt className="h-5 w-5 text-purple-600" />
                      {esEntregado ? 'Prendas Entregadas' : 'Prendas Alistadas'}
                    </h3>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                        {getTotalItems()} items
                      </Badge>
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        {getTotalKilos()} kg
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-900">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400">
                            Item
                          </th>
                          <th className="px-4 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-400">
                            Peso (g)
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 dark:text-gray-400">
                            Cantidad
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {entrega.items?.map((item, idx) => (
                          <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-900">
                            <td className="px-4 py-3 text-sm">{item.nombre}</td>
                            <td className="px-4 py-3 text-sm text-center text-gray-600">
                              {item.peso || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-right font-semibold">{item.cantidad}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Firma */}
                {entrega.firma && (
                  <div className="mb-6">
                    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      <FileCheck className="h-5 w-5 text-green-600" />
                      Firma del Receptor
                    </h3>
                    <div className="border-2 border-green-200 dark:border-green-800 rounded-lg p-4 bg-gradient-to-br from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20">
                      <img 
                        src={entrega.firma} 
                        alt="Firma del receptor" 
                        className="max-w-full h-40 mx-auto object-contain bg-white dark:bg-gray-800 rounded p-2"
                        onError={(e) => {
                          console.error('Error al cargar firma:', entrega.firma);
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = `
                            <div class="text-center text-red-600 dark:text-red-400 p-4">
                              <p class="font-semibold text-lg mb-2">⚠️ Error al cargar la firma</p>
                              <p class="text-sm mt-2 break-all">URL: ${entrega.firma}</p>
                            </div>
                          `;
                        }}
                        onLoad={() => {
                          console.log('✅ Firma cargada correctamente:', entrega.firma);
                        }}
                      />
                      <p className="text-center text-xs text-gray-600 dark:text-gray-400 mt-2">
                        {entrega.receptor_nombre && `Firmado por: ${entrega.receptor_nombre}`}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between gap-3 p-6 border-t bg-gray-50 dark:bg-gray-900">
                <PrintEntregaButtons 
                  entrega={solicitudFormateada}
                  disabled={false}
                />
                <Button
                  variant="outline"
                  onClick={onClose}
                >
                  Cerrar
                </Button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default DetalleEntregaVerticalModal;
